/**
 * diff.ts — per-session cue detection state + `diffCues` (v2M8.1+).
 *
 * Mirrors the `vfx.ts` snapshot-and-diff pattern: every gameState
 * transition is observed between frames, and any audible event
 * (weapon fire, ship destruction, jump in/out, structure complete)
 * yields a `CueEvent` to be played by `AudioEngine.onFrame`.
 *
 * Determinism note: `diffCues` is purely a function of gameState
 * transitions + an `AudioFrameContext` snapshot of the camera / view
 * mode used to compute positional opts. It never reads RNG or wall-
 * clock time. Cue *playback* timing (envelope decay, fade) lives in
 * the procedural builder and runs on `audioContext.currentTime` per
 * AUDIO §4.1 — that's a post-determinism concern.
 *
 * v2M8.4 wires the first sim-driven cues:
 *   - **Weapon fire** — diff `ship.lastFiredTick` against the snapshot.
 *   - **Weapon impact** — diff `ship.currentHull` / `currentShields`
 *     against the snapshot. Damage-type inferred from the firer that
 *     targeted this ship this tick (via `lastFiredTargetShipId`).
 *
 * Per-tick positional ops:
 *   - Pan from screen-x relative to camera centre (clamped ±1).
 *   - Attenuation as inverse-square of distance from camera centre,
 *     silent beyond 1.5× viewport diagonal in screen pixels (mirrors
 *     AUDIO §9.1 "1.5× screen diagonal in world units").
 *
 * View-mode + system gating per AUDIO §9.2 / §9.3:
 *   - Galaxy view suppresses all weapon cues.
 *   - System view suppresses cues for ships in any system other than
 *     the focused one.
 */

import type {
  EmpireId,
  EntityId,
  GameState,
  Missile,
  MissileId,
  PlanetId,
  ShipInstance,
  ShipInstanceId,
  SystemId,
} from '../state/types/index';
import { missileFamilyFromWeaponName } from '../game/advance-missiles';
import type { CueId } from './registry';
import type { PlayCueOpts } from './engine';

export interface CueEvent {
  cueId: CueId;
  opts: PlayCueOpts;
}

/**
 * Camera + viewport context the diff path needs to compute positional
 * opts. Built per frame by the caller (canvas.ts) and passed into
 * `onFrame` / `diffCues`. Audio module deliberately knows nothing
 * about the `Camera` / `Viewport` types — keeps the audio layer
 * decoupled from the renderer.
 */
export interface AudioFrameContext {
  /** Current view — galaxy mode suppresses spatial cues per §9.2. */
  viewMode: 'galaxy' | 'system';
  /** System currently focused; cues for ships elsewhere suppressed per §9.3. */
  focusedSystemId: SystemId | null;
  /**
   * Viewer empire — drives v2M8.5 fog-of-war gating. Cues for ships
   * the viewer can't see (no `teamVision` row, or `seenVia !== 'vision'`)
   * are suppressed so audio doesn't leak hidden positions. Mirrors
   * `canViewerSeeShip` in `vfx.ts`.
   */
  viewerEmpireId: EmpireId;
  /** World-to-screen projection, derived from the active system camera. */
  worldToScreen: (sx: number, sy: number) => { x: number; y: number };
  /** Viewport bounds in screen pixels — drives pan + attenuation math. */
  viewportCentreX: number;
  viewportCentreY: number;
  viewportWidth: number;
  viewportHeight: number;
}

/**
 * Per-session snapshot state used by `diffCues` to detect transitions.
 * `lastObservedTick` gates the diff so multiple render frames within a
 * single sim tick don't re-detect the same event.
 */
export interface DiffState {
  lastObservedTick: number;
  /** Per-ship snapshot of `lastFiredTick` — drives fire detection. */
  lastFiredTickByShip: Map<ShipInstanceId, number | null>;
  /** Per-ship hull from the previous tick — drives hull-impact detection. */
  prevHullByShip: Map<ShipInstanceId, number>;
  /** Per-ship shields from the previous tick — drives shield-impact detection. */
  prevShieldByShip: Map<ShipInstanceId, number>;
  /**
   * v2M8.5 — full per-ship pose snapshot used by the lifecycle-cue
   * detection (destruction / hyperspace / cloak / throttle-up). When
   * a ship vanishes from `gameState.ships` we still need its
   * last-known position + owner to fire `ship:destroyed` at the
   * right pan / FoW-gated correctly.
   */
  prevShipSnapshot: Map<ShipInstanceId, ShipPoseSnap>;
  /**
   * v2M8.6 — per-structure snapshot used by structure-cue detection
   * (construction-start / complete / destroyed). Carries the
   * `planetId` so a destroyed structure's position can still be
   * resolved via the planet (planets don't vanish in v1).
   */
  prevStructureSnapshot: Map<EntityId, StructureSnap>;
  /**
   * MFix3.6 — per-planet ownership snapshot used by colonisation-cue
   * detection. A transition `null → empireId` fires `colony:established`.
   * Captures every planet (including currently-uninhabited ones) so the
   * first owned-state snapshot doesn't spuriously emit on first frame —
   * the size-zero first-frame guard in `diffCues` already handles that.
   */
  prevPlanetOwnerByPlanet: Map<PlanetId, EmpireId | null>;
  /**
   * v3M28.5 — per-missile snapshot used by the missile launch / impact
   * cue dispatch. New entries (id not in snapshot) emit a per-family
   * launch cue at the launcher position; gone entries emit a per-family
   * impact cue at the target position iff the missile would have hit
   * (`willHit === true`). Misses + target-gone despawns are silent per
   * `MISSILES.md §7`. The full `Missile` value is kept (not just the
   * id) so the diff knows family + willHit + last-known position at
   * despawn time.
   */
  prevMissileById: Map<MissileId, Missile>;
}

interface ShipPoseSnap {
  sx: number;
  sy: number;
  systemId: SystemId | null;
  ownerEmpireId: EmpireId;
  cloaked: boolean;
  hadMoveOrder: boolean;
}

interface StructureSnap {
  planetId: PlanetId;
  level: number;
  buildTicksRemaining: number;
}

export function createDiffState(): DiffState {
  return {
    lastObservedTick: -1,
    lastFiredTickByShip: new Map(),
    prevHullByShip: new Map(),
    prevShieldByShip: new Map(),
    prevShipSnapshot: new Map(),
    prevStructureSnapshot: new Map(),
    prevPlanetOwnerByPlanet: new Map(),
    prevMissileById: new Map(),
  };
}

type DamageType = 'Energy' | 'Kinetic' | 'Concussion' | 'EMP';

/**
 * v3M28.6 playtest tuning — the commissioned ElevenLabs launch SFX
 * peak-normalises hot. Multiplies into the launch-cue `gainMul`
 * (alongside the deterministic per-tick±3 dB variation) so launches
 * sit comfortably under the impact SFX without lowering the SFX bus
 * (which would attenuate impacts too). 0.5 → -6 dB; tune up to 0.7
 * (-3 dB) or down to 0.4 (-8 dB) if subsequent playtest signal moves
 * the cue.
 */
const MISSILE_LAUNCH_GAIN_MUL = 0.5;

const DAMAGE_TYPE_TO_PREFIX: Record<DamageType, 'energy' | 'kinetic' | 'concussion' | 'emp'> = {
  Energy: 'energy',
  Kinetic: 'kinetic',
  Concussion: 'concussion',
  EMP: 'emp',
};

/**
 * Diff against the previous frame and return a list of fire-once cue
 * events. Refreshes the snapshot at the end. No-op when the sim hasn't
 * advanced since the last call (mirrors `advanceVfx`'s tick guard).
 */
export function diffCues(
  state: DiffState,
  gameState: GameState,
  ctx?: AudioFrameContext,
): CueEvent[] {
  if (gameState.tick === state.lastObservedTick) return [];
  state.lastObservedTick = gameState.tick;

  // First frame: snapshot only, no events. Without this we'd see every
  // ship as "currently firing for the first time" / "currently cloaked
  // for the first time" / etc. and emit a cue. Same applies to every
  // existing structure being observed as "just queued" and every owned
  // planet being observed as "just colonised".
  if (
    state.prevShipSnapshot.size === 0 &&
    state.prevStructureSnapshot.size === 0 &&
    state.prevPlanetOwnerByPlanet.size === 0
  ) {
    snapshotShips(state, gameState);
    snapshotStructures(state, gameState);
    snapshotPlanets(state, gameState);
    return [];
  }

  const events: CueEvent[] = [];

  // Skip weapon detection entirely in galaxy view (§9.2) or when the
  // caller didn't pass a context (test paths).
  const weaponsAudible = ctx !== undefined && ctx.viewMode === 'system';

  // Pre-build a map of "firers targeting ship X this tick" so impact
  // detection can look up the inflicting damage type.
  const firersByTarget = new Map<ShipInstanceId, ShipInstance[]>();
  for (const ship of gameState.ships.values()) {
    if (ship.lastFiredTick === null) continue;
    const prev = state.lastFiredTickByShip.get(ship.id) ?? null;
    if (ship.lastFiredTick === prev) continue;
    if (ship.lastFiredTargetShipId === null) continue;
    const list = firersByTarget.get(ship.lastFiredTargetShipId) ?? [];
    list.push(ship);
    firersByTarget.set(ship.lastFiredTargetShipId, list);
  }

  // Fire cues — one per ship whose `lastFiredTick` advanced.
  if (weaponsAudible) {
    for (const ship of gameState.ships.values()) {
      const prev = state.lastFiredTickByShip.get(ship.id) ?? null;
      if (ship.lastFiredTick === prev) continue;
      if (ship.lastFiredTick === null) continue;
      if (ship.systemId !== ctx.focusedSystemId) continue;
      if (!canViewerSeeShip(gameState, ctx.viewerEmpireId, ship.id, ship.ownerEmpireId)) continue;
      // v3M28.5 — suppress the generic concussion fire cue for ships
      // whose just-fired weapon is a Missile subtype. The per-family
      // `weapon:missile:launch:*` cue dispatched from the
      // state.projectiles diff below handles the audible event; double-
      // firing both would be jarring and louder than intended.
      if (firstNonEmpWeaponSubType(ship, gameState) === 'Missile') continue;
      const damageType = inferDamageType(ship, gameState);
      const variation = hashVariation(gameState.tick, ship.id);
      const positional = positionalOpts(ctx, ship.sx, ship.sy);
      if (positional === null) continue;
      events.push({
        cueId: `weapon:${DAMAGE_TYPE_TO_PREFIX[damageType]}:fire`,
        opts: { ...positional, ...variation },
      });
    }
  }

  // Impact cues — diff hull / shield against snapshot, look up damage
  // type from `firersByTarget`. Skip when the target is in a different
  // system or galaxy view is active.
  if (weaponsAudible) {
    for (const ship of gameState.ships.values()) {
      if (ship.systemId !== ctx.focusedSystemId) continue;
      if (!canViewerSeeShip(gameState, ctx.viewerEmpireId, ship.id, ship.ownerEmpireId)) continue;
      const prevHull = state.prevHullByShip.get(ship.id);
      const prevShield = state.prevShieldByShip.get(ship.id);
      if (prevHull === undefined || prevShield === undefined) continue;
      const hullDrop = prevHull - ship.currentHull;
      const shieldDrop = prevShield - ship.currentShields;
      if (hullDrop <= 0 && shieldDrop <= 0) continue;
      // Damage type — use the first firer that targeted this ship this
      // tick. If none (e.g. damage from bombardment / surface battery),
      // fall back to Energy. Battery cues are a v3M11 polish item.
      const firers = firersByTarget.get(ship.id);
      const firer = firers?.[0];
      const damageType: DamageType = firer ? inferDamageType(firer, gameState) : 'Energy';
      const variation = hashVariation(gameState.tick, ship.id);
      const positional = positionalOpts(ctx, ship.sx, ship.sy);
      if (positional === null) continue;
      const prefix = DAMAGE_TYPE_TO_PREFIX[damageType];
      if (shieldDrop > 0) {
        events.push({
          cueId: `weapon:${prefix}:shield`,
          opts: { ...positional, ...variation },
        });
      }
      if (hullDrop > 0) {
        events.push({
          cueId: `weapon:${prefix}:hull`,
          opts: { ...positional, ...variation },
        });
      }
    }
  }

  // v2M8.5 — ship lifecycle cues. Same in-system / FoW gating as
  // weapon cues. Six events per AUDIO §7 + §8 paired with v2M7 VFX:
  //   - Destruction (ship vanishes; prior systemId non-null).
  //   - Hyperspace out / in (systemId null transitions).
  //   - Cloak engage / disengage (cloaked transitions).
  //   - Throttle-up (moveOrder null → non-null).
  if (weaponsAudible) {
    // Destruction first — iterate prev snapshot, look for ships gone
    // from current state. Position comes from the prev snap since the
    // ship is no longer in `gameState.ships`.
    for (const [id, snap] of state.prevShipSnapshot) {
      if (gameState.ships.has(id)) continue;
      if (snap.systemId === null) continue;
      if (snap.systemId !== ctx.focusedSystemId) continue;
      if (!canViewerSeeShip(gameState, ctx.viewerEmpireId, id, snap.ownerEmpireId)) continue;
      // MFix13.3b/.3c — graceful despawns (carrier squadron recall, etc.)
      // skip the `ship:destroyed` cue. Same gate as vfx.ts destruction
      // dispatcher.
      if (gameState.gracefulDespawnIds?.has(id)) continue;
      const positional = positionalOpts(ctx, snap.sx, snap.sy);
      if (positional === null) continue;
      events.push({ cueId: 'ship:destroyed', opts: positional });
    }
    // Remaining lifecycle events fire on still-alive ships whose state
    // transitioned this tick.
    for (const ship of gameState.ships.values()) {
      const snap = state.prevShipSnapshot.get(ship.id);
      if (!snap) continue;
      const wasInSystem = snap.systemId !== null;
      const isInSystem = ship.systemId !== null;
      // Hyperspace out — fire at last in-system position even though
      // the ship's `systemId` is now null.
      if (wasInSystem && !isInSystem && snap.systemId === ctx.focusedSystemId) {
        if (canViewerSeeShip(gameState, ctx.viewerEmpireId, ship.id, ship.ownerEmpireId)) {
          const positional = positionalOpts(ctx, snap.sx, snap.sy);
          if (positional !== null) {
            events.push({ cueId: 'ship:hyperspace-out', opts: positional });
          }
        }
        continue;
      }
      // Hyperspace in — fire at arrival position.
      if (!wasInSystem && isInSystem && ship.systemId === ctx.focusedSystemId) {
        if (canViewerSeeShip(gameState, ctx.viewerEmpireId, ship.id, ship.ownerEmpireId)) {
          const positional = positionalOpts(ctx, ship.sx, ship.sy);
          if (positional !== null) {
            events.push({ cueId: 'ship:hyperspace-in', opts: positional });
          }
        }
        continue;
      }
      // Cloak / throttle events apply only to ships that stayed
      // in-system and stayed observable (cloak-disengage included —
      // a ship the player has vision of going visible is still a cue;
      // a ship the player CAN'T see at all stays silent).
      if (ship.systemId !== ctx.focusedSystemId) continue;
      if (!canViewerSeeShip(gameState, ctx.viewerEmpireId, ship.id, ship.ownerEmpireId)) continue;
      const positional = positionalOpts(ctx, ship.sx, ship.sy);
      if (positional === null) continue;
      if (snap.cloaked !== ship.cloaked) {
        events.push({
          cueId: ship.cloaked ? 'ship:cloak-engage' : 'ship:cloak-disengage',
          opts: positional,
        });
      }
      const hasMoveOrder = ship.moveOrder !== null;
      if (!snap.hadMoveOrder && hasMoveOrder) {
        events.push({ cueId: 'ship:throttle-up', opts: positional });
      }
    }
  }

  // v2M8.6 — structure cues. Same in-system gating as weapons /
  // lifecycle (galaxy-view + cross-system suppress per §9.2 + §9.3).
  // Three single-shot events: construction-start (positional),
  // complete (non-positional), destroyed (positional). Planet-side
  // structures only — space-structure construction is already covered
  // by ship:throttle-up + ship:hyperspace from v2M8.5.
  if (weaponsAudible) {
    // New entries + completion transitions on still-existing entries.
    for (const struct of gameState.structures.values()) {
      const planet = gameState.planets.get(struct.planetId);
      if (!planet || planet.systemId !== ctx.focusedSystemId) continue;
      const prev = state.prevStructureSnapshot.get(struct.id);
      if (!prev) {
        // Newly observed entry.
        if (struct.buildTicksRemaining > 0) {
          const positional = positionalOpts(ctx, planet.systemX, planet.systemY);
          if (positional !== null) {
            events.push({ cueId: 'structure:construction-start', opts: positional });
          }
        }
        continue;
      }
      // Build / upgrade just completed this tick.
      if (prev.buildTicksRemaining > 0 && struct.buildTicksRemaining === 0) {
        events.push({ cueId: 'structure:complete', opts: {} });
      }
    }
    // Destruction — entries gone from `state.structures`.
    for (const [id, snap] of state.prevStructureSnapshot) {
      if (gameState.structures.has(id)) continue;
      const planet = gameState.planets.get(snap.planetId);
      if (!planet || planet.systemId !== ctx.focusedSystemId) continue;
      const positional = positionalOpts(ctx, planet.systemX, planet.systemY);
      if (positional === null) continue;
      events.push({ cueId: 'structure:destroyed', opts: positional });
    }
  }

  // MFix3.6 — colonisation cues. A planet transitions from
  // `ownerEmpireId === null` to a non-null empireId when either a
  // Coloniser lands (MFix3.3) or a planet flips via Loyalty conquest
  // (v2M5). Same in-system gating as the other positional cues; FoW
  // is not applied because planets don't carry a vision record (every
  // empire can see every planet's ownership state).
  if (weaponsAudible) {
    for (const planet of gameState.planets.values()) {
      if (planet.systemId !== ctx.focusedSystemId) continue;
      const prev = state.prevPlanetOwnerByPlanet.get(planet.id);
      if (prev === undefined) continue; // newly observed, no transition
      if (prev !== null) continue; // was owned previously
      if (planet.ownerEmpireId === null) continue; // still uninhabited
      const positional = positionalOpts(ctx, planet.systemX, planet.systemY);
      if (positional === null) continue;
      events.push({ cueId: 'colony:established', opts: positional });
    }
  }

  // v3M28.5 — missile launch + impact cues. New entries in
  // `state.projectiles` fire a per-family launch cue at the launcher
  // position; gone entries that would have hit (`willHit === true`)
  // fire a per-family impact cue at the target position. Misses +
  // target-gone despawns are silent per `MISSILES.md §7`.
  if (weaponsAudible) {
    // New: emit launch cue for every projectile id in current but not
    // snapshot. Family discriminant chooses between the v3M28.5 Missile
    // sub-family cues (`weapon:missile:launch:<pulson|zeon>`) and the
    // v4M9.2.4 Torpedo cue (`weapon:torpedo:launch`).
    for (const missile of gameState.projectiles.values()) {
      if (state.prevMissileById.has(missile.id)) continue;
      if (missile.systemId !== ctx.focusedSystemId) continue;
      // v4M9.3.5 — Pulse already triggers `weapon:energy:fire` from the
      // WeaponFireEvent diff path; its impact triggers `weapon:energy:*`
      // from the DamageEvent path. Skipping Pulse here avoids double-
      // dispatching a Missile/Torpedo-style cue alongside the existing
      // Energy cue chain. (Missile + Torpedo go through this loop
      // because they have no DamageEvent at fire-time — the projectile
      // path is their only audio surface.)
      //
      // v4M9.4.5 — Burst rides `weapon:kinetic:*` from the same dual
      // (fire-event + damage-event) cue chain as Pulse. Same skip.
      if (missile.family === 'Pulse' || missile.family === 'Burst') continue;
      const variation = hashVariation(gameState.tick, missile.id);
      const positional = positionalOpts(ctx, missile.sx0, missile.sy0);
      if (positional === null) continue;
      const cueId =
        missile.family === 'Torpedo'
          ? 'weapon:torpedo:launch'
          : (`weapon:missile:launch:${missileFamilyFromWeaponName(missile.weaponName)}` as const);
      events.push({
        cueId,
        opts: {
          ...positional,
          ...variation,
          gainMul: variation.gainMul * MISSILE_LAUNCH_GAIN_MUL,
        },
      });
    }
    // Gone: emit impact cue for every projectile id in snapshot but not
    // current. Silent if `willHit === false` or if the snapshot entry
    // was in a different system from focusedSystemId.
    for (const [id, prev] of state.prevMissileById) {
      if (gameState.projectiles.has(id)) continue;
      if (!prev.willHit) continue;
      if (prev.systemId !== ctx.focusedSystemId) continue;
      // v4M9.3.5 — Pulse impact audio rides the DamageEvent path; skip
      // here to avoid the same double-dispatch the launch loop avoids.
      // v4M9.4.5 — Same for Burst (Kinetic cue chain).
      if (prev.family === 'Pulse' || prev.family === 'Burst') continue;
      const variation = hashVariation(gameState.tick, id);
      const positional = positionalOpts(ctx, prev.sx1, prev.sy1);
      if (positional === null) continue;
      const cueId =
        prev.family === 'Torpedo'
          ? 'weapon:torpedo:impact'
          : (`weapon:missile:impact:${missileFamilyFromWeaponName(prev.weaponName)}` as const);
      events.push({
        cueId,
        opts: { ...positional, ...variation },
      });
    }
  }

  // Refresh snapshots for next tick.
  snapshotShips(state, gameState);
  snapshotStructures(state, gameState);
  snapshotPlanets(state, gameState);
  snapshotMissiles(state, gameState);
  return events;
}

function snapshotMissiles(state: DiffState, gameState: GameState): void {
  state.prevMissileById.clear();
  for (const missile of gameState.projectiles.values()) {
    // Shallow clone — Missile is plain data so a spread suffices.
    state.prevMissileById.set(missile.id, { ...missile });
  }
}

/**
 * True if the viewer can currently see the given ship. Mirrors the
 * v2M7.6b `canViewerSeeShip` in `vfx.ts` — own ships always visible;
 * other empires' ships only with an active `seenVia === 'vision'`
 * row in `teamVision`. Duplicated here rather than imported so the
 * audio layer doesn't pull on the render layer.
 */
function canViewerSeeShip(
  gameState: GameState,
  viewerId: EmpireId,
  shipId: ShipInstanceId,
  ownerEmpireId: EmpireId,
): boolean {
  if (ownerEmpireId === viewerId) return true;
  const viewerVision = gameState.teamVision.get(viewerId);
  if (!viewerVision) return false;
  const rec = viewerVision.get(shipId);
  return rec?.seenVia === 'vision';
}

function snapshotShips(state: DiffState, gameState: GameState): void {
  state.lastFiredTickByShip.clear();
  state.prevHullByShip.clear();
  state.prevShieldByShip.clear();
  state.prevShipSnapshot.clear();
  for (const ship of gameState.ships.values()) {
    state.lastFiredTickByShip.set(ship.id, ship.lastFiredTick);
    state.prevHullByShip.set(ship.id, ship.currentHull);
    state.prevShieldByShip.set(ship.id, ship.currentShields);
    state.prevShipSnapshot.set(ship.id, {
      sx: ship.sx,
      sy: ship.sy,
      systemId: ship.systemId,
      ownerEmpireId: ship.ownerEmpireId,
      cloaked: ship.cloaked,
      hadMoveOrder: ship.moveOrder !== null,
    });
  }
}

function snapshotStructures(state: DiffState, gameState: GameState): void {
  state.prevStructureSnapshot.clear();
  for (const struct of gameState.structures.values()) {
    state.prevStructureSnapshot.set(struct.id, {
      planetId: struct.planetId,
      level: struct.level,
      buildTicksRemaining: struct.buildTicksRemaining,
    });
  }
}

function snapshotPlanets(state: DiffState, gameState: GameState): void {
  state.prevPlanetOwnerByPlanet.clear();
  for (const planet of gameState.planets.values()) {
    state.prevPlanetOwnerByPlanet.set(planet.id, planet.ownerEmpireId);
  }
}

/**
 * Best-effort damage-type inference for a firing ship. Walks the
 * ship's weapon loadout and returns the first non-EMP weapon's
 * damage type; falls back to Energy. Mirrors `inferWeaponForShip`
 * in `vfx.ts` — duplicated here rather than imported so the audio
 * layer doesn't pull on the render layer.
 */
function inferDamageType(
  ship: { ownerEmpireId: number; chassis: string },
  gameState: GameState,
): DamageType {
  const empire = gameState.empires.get(ship.ownerEmpireId);
  if (!empire) return 'Energy';
  const tmpl = gameState.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return 'Energy';
  for (const slot of tmpl.weaponLoadout) {
    const weapon = gameState.weapons.get(slot.weapon);
    if (weapon && weapon.damageType !== 'EMP') return weapon.damageType;
  }
  return 'Energy';
}

/** v3M28.5 — return the sub-type of the ship's first non-EMP weapon,
 *  used to suppress the generic concussion-fire cue when the ship
 *  just fired a missile (avoids double-firing alongside the
 *  per-family `weapon:missile:launch:*` cue). */
function firstNonEmpWeaponSubType(
  ship: { ownerEmpireId: number; chassis: string },
  gameState: GameState,
): string | null {
  const empire = gameState.empires.get(ship.ownerEmpireId);
  if (!empire) return null;
  const tmpl = gameState.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return null;
  for (const slot of tmpl.weaponLoadout) {
    const weapon = gameState.weapons.get(slot.weapon);
    if (weapon && weapon.damageType !== 'EMP') return weapon.subType;
  }
  return null;
}

/**
 * Compute the §6.1 deterministic variation seeded from sim tick + ship
 * id. ±15 % pitch range (linear 0.85..1.15) and ±3 dB gain range
 * (linear 0.71..1.41). Hashed with two independent mixers so pitch and
 * gain don't track each other.
 */
function hashVariation(
  tick: number,
  shipId: ShipInstanceId,
): { pitchMul: number; gainMul: number } {
  const seed = (tick * 73856093) ^ (shipId * 19349663);
  const h1 = ((seed * 2654435761) >>> 0) / 0x100000000;
  const h2 = ((seed * 40503 + 13) >>> 0) / 0x100000000;
  return {
    pitchMul: 0.85 + h1 * 0.3,
    gainMul: Math.pow(10, ((h2 - 0.5) * 6) / 20),
  };
}

/**
 * Compute pan + attenuation for a world-space (sx, sy) given the
 * frame context. Returns `null` when the source is so far off-camera
 * that attenuation hits zero — caller can then skip the cue.
 *
 * Pan is screen-x relative to camera centre, clamped ±1. Attenuation
 * is `1 - clamp(d/maxD, 0, 1)^2` where `d` is screen-pixel distance
 * from the centre and `maxD` is 1.5× the viewport diagonal — yields
 * full volume at centre, silent at the off-screen edge.
 */
function positionalOpts(
  ctx: AudioFrameContext,
  sx: number,
  sy: number,
): { panX: number; attenuation: number } | null {
  const screen = ctx.worldToScreen(sx, sy);
  const dx = screen.x - ctx.viewportCentreX;
  const dy = screen.y - ctx.viewportCentreY;
  const dist = Math.hypot(dx, dy);
  const diagonal = Math.hypot(ctx.viewportWidth, ctx.viewportHeight);
  const maxD = diagonal * 1.5;
  if (dist >= maxD) return null;
  const t = dist / maxD;
  const attenuation = Math.max(0, 1 - t * t);
  const halfWidth = ctx.viewportWidth / 2 || 1;
  const panX = Math.max(-1, Math.min(1, dx / halfWidth));
  return { panX, attenuation };
}
